import type { EditorInteractionGroupName } from './interaction-events';
import { interactionEventKind } from './interaction-events';

/**
 * The Event Timing fields this package reads. `interactionId` is missing from the DOM
 * typings' `PerformanceEventTiming`, so it is declared here as optional, which also makes
 * a `PerformanceEntry` from `getEntries()` assignable without a cast.
 */
export type InteractionEntry = PerformanceEntry & { interactionId?: number };

/**
 * What an entry did to the interaction it belongs to: either it is the first entry of a new
 * interaction, or it measured an interaction that was already counted as slower than it was
 * known to be. Both carry the editor group of the interaction, if it is one of the editor's.
 */
export type InteractionUpdate =
	| { group: EditorInteractionGroupName | undefined; latencyMs: number; type: 'new' }
	| {
			fromMs: number;
			group: EditorInteractionGroupName | undefined;
			toMs: number;
			type: 'remeasured';
	  };

type TrackedInteraction = {
	group: EditorInteractionGroupName | undefined;
	latencyMs: number;
};

/**
 * How many interactions are remembered, so their growth can still be applied, and how many of
 * the editor's events. Entries of one interaction arrive within the interaction itself, so
 * anything older than the last few hundred is not needed.
 */
const MAX_TRACKED = 256;
/**
 * How many are dropped per cleanup. Dropping one at a time would run a cleanup on every new
 * interaction once the limit is reached; a batch makes it one cleanup per 64 of them.
 */
const PRUNE_BATCH = 64;

/**
 * Identifies the event an entry measured: an entry's `startTime` is that event's timestamp and its
 * `name` is its type. The type is in the key because browsers coarsen the timestamp, so two events
 * of one task can share it — and events of one type are always of one group, so a collision cannot
 * move an interaction into another group.
 */
function eventKey(type: string, timeStamp: number): string {
	return `${type}|${timeStamp}`;
}

/**
 * Makes interactions out of what the two observers report, for one session.
 *
 * Entries sharing a non-zero `interactionId` are one interaction whose latency is the
 * maximum `duration` among them. Entries arrive incrementally, so an interaction's latency
 * can grow after it was first reported — callers apply that to what they already counted
 * rather than counting the interaction twice.
 *
 * A non-zero `interactionId` is the browser's own definition of an interaction, which is
 * also what INP filters on: it is assigned to the pointer and keyboard events that make one
 * up, and never to scrolling or pointer movement.
 *
 * The editor's events answer what an entry cannot: which interactions were with the editor, and
 * how many there were, including the ones below the Event Timing reporting threshold.
 */
export class InteractionTracker {
	private readonly startsAfterInteractionId: number;

	private readonly interactions = new Map<number, TrackedInteraction>();
	private readonly groupByEvent = new Map<string, EditorInteractionGroupName>();

	private highestInteractionId = 0;

	/**
	 * @param startsAfterInteractionId interactions up to and including this one belong to the
	 * previous tracker and are ignored. `interactionId` counts up over the life of the page, so
	 * a session opening mid-page passes the highest id the one before it saw; without that, an
	 * entry still arriving for an interaction from the previous session would look new here and
	 * be counted in both.
	 */
	constructor(startsAfterInteractionId = 0) {
		this.startsAfterInteractionId = startsAfterInteractionId;
	}

	/** The highest `interactionId` this tracker has seen. */
	get lastInteractionId(): number {
		return Math.max(this.highestInteractionId, this.startsAfterInteractionId);
	}

	/**
	 * Merges an entry into the interaction it belongs to.
	 *
	 * @returns what that did to the interaction's latency, or nothing when the entry is not
	 * part of an interaction, belongs to a previous tracker, or does not change one.
	 */
	merge(entry: InteractionEntry): InteractionUpdate | undefined {
		const { interactionId } = entry;
		// `first-input` and non-interaction events report `interactionId` 0.
		if (!interactionId) {
			return undefined;
		}

		if (interactionId <= this.startsAfterInteractionId) {
			return undefined;
		}

		this.highestInteractionId = Math.max(this.highestInteractionId, interactionId);

		// Every latency is counted through here, so this is where one that cannot be measured
		// is rejected: a `NaN` getting through becomes a `NaN` bucket key and a `NaN` `sumMs`
		// for the rest of the session.
		if (!Number.isFinite(entry.duration) || entry.duration < 0) {
			return undefined;
		}

		const tracked = this.interactions.get(interactionId);

		if (tracked === undefined) {
			// Taken once: an entry that only makes the interaction slower has to move its count
			// within the group it was counted in, not into another one.
			const group = this.groupByEvent.get(eventKey(entry.name, entry.startTime));
			this.interactions.set(interactionId, { latencyMs: entry.duration, group });
			this.prune(this.interactions);
			return { type: 'new', latencyMs: entry.duration, group };
		}

		if (entry.duration <= tracked.latencyMs) {
			return undefined;
		}

		const fromMs = tracked.latencyMs;
		tracked.latencyMs = entry.duration;
		return { type: 'remeasured', fromMs, toMs: entry.duration, group: tracked.group };
	}

	/** @returns the group of an interaction to count, when this is the event its group counts on. */
	recordEditorEvent(event: Event): EditorInteractionGroupName | undefined {
		const kind = interactionEventKind(event.type);
		if (!kind) {
			return undefined;
		}

		// Every event of the interaction, because any of them can be the one Event Timing reports
		// as the slowest: for a pointer press that is usually the click.
		this.groupByEvent.set(eventKey(event.type, event.timeStamp), kind.group);
		this.prune(this.groupByEvent);

		return kind.counts ? kind.group : undefined;
	}

	private prune<Key>(entries: Map<Key, unknown>): void {
		if (entries.size <= MAX_TRACKED) {
			return;
		}

		// `Map` preserves insertion order, so the entries inserted first are the least likely to
		// see another entry or another event.
		let remaining = PRUNE_BATCH;
		for (const key of entries.keys()) {
			entries.delete(key);
			remaining -= 1;
			if (remaining === 0) {
				return;
			}
		}
	}
}
